/**
 * A request that got no answer (D-144, rule 8 of
 * documentation/ui-qa/notifications/connection-health-acceptance.md).
 *
 * "No answer" is one signal: the request was refused at the network or timed out, after the API
 * client's own retries, and nobody cancelled it on purpose. It is a connection matter, never the
 * page's. The API client marks such a failure `unanswered` and reports it here once; the connection
 * monitor, wired in app-index, decides whether it was a single failure (one toast) or an outage (the
 * offline overlay). A page that receives a marked failure presents nothing and leaves its state as
 * it was before the action, and `isUnanswered` is how it asks.
 *
 * A first load has no earlier state to keep, so it waits instead: `reloadWhenServerAnswers` runs it
 * again when the monitor next sees the server answer, through the signal plugged in here.
 *
 * The client reports through this module rather than importing the monitor, the same way it reaches
 * the update gate through services/app-update: the client stays the bottom layer, and whatever
 * listens is plugged in from above.
 */

type UnansweredRequestListener = () => void;

let listener: UnansweredRequestListener | undefined;

/**
 * Plug in what happens when a request gets no answer. One listener: the application has one
 * connection monitor, and a second registration replaces the first rather than doubling every check.
 * Passing undefined unplugs it.
 */
export function setUnansweredRequestListener(
  next: UnansweredRequestListener | undefined
): void {
  listener = next;
}

/**
 * Called by the API client, once per request that ended unanswered. A listener that throws must not
 * turn a failed request into a thrown one, so its error is kept here.
 */
export function reportUnansweredRequest(): void {
  try {
    listener?.();
  } catch (error) {
    console.error('[unanswered-request] listener failed', error);
  }
}

type ServerAnswersSignal = () => Promise<void>;

let serverAnswers: ServerAnswersSignal | undefined;

/**
 * Plug in the signal a first load waits on: the connection monitor's `whenReachable`, wired in
 * app-index like the listener above. Passing undefined unplugs it.
 */
export function setServerAnswersSignal(
  next: ServerAnswersSignal | undefined
): void {
  serverAnswers = next;
}

/** A first load waiting to be run again, as `reloadWhenServerAnswers` takes it. */
export interface FirstLoadRetry {
  /** The page or component whose load it is; the wait is dropped once it leaves the document. */
  host: { readonly isConnected: boolean };
  /** Runs the load again. */
  reload: () => unknown;
  /**
   * False once a newer load has started, which then owns the page and this wait is dropped. Pages
   * with a load sequence pass a check of it; without one, the host's own dedupe below applies.
   */
  isCurrent?: () => boolean;
}

/** The newest retry each host is waiting to run; one wait per host, whatever fails meanwhile. */
const waiting = new WeakMap<object, FirstLoadRetry>();

/**
 * A first load that got no answer (rule 8's first-load clause, D-144): the page keeps its loading
 * state, presents nothing, and calls this. When the connection monitor next sees the server
 * answer, the load runs again, once. A newer load (`isCurrent` false) or the host leaving the
 * document cancels it. Several failures on one host before the server answers share one wait and
 * run the newest reload, so a page whose three first-load requests all went unanswered loads once.
 *
 * With no signal plugged in (a unit test, or before boot wires the monitor) nothing is scheduled
 * and the page stays loading, which is the state rule 8 asks for until the server answers.
 */
export function reloadWhenServerAnswers(retry: FirstLoadRetry): void {
  const signal = serverAnswers;
  if (!signal) return;
  const alreadyWaiting = waiting.has(retry.host);
  waiting.set(retry.host, retry);
  if (alreadyWaiting) return;
  void signal().then(() => {
    const latest = waiting.get(retry.host);
    waiting.delete(retry.host);
    if (!latest || !latest.host.isConnected) return;
    if (latest.isCurrent && !latest.isCurrent()) return;
    latest.reload();
  });
}

/**
 * A second host for `owner`, for a wait that must neither replace nor be replaced by the owner's
 * own wait (`reloadWhenServerAnswers` keeps one wait per host, and the newest registration wins):
 * the product page's series read beside its product re-read, say. It counts as connected exactly
 * while the owner is, so the wait is still dropped once the owner leaves.
 */
export function separateWaitHost(owner: { readonly isConnected: boolean }): {
  readonly isConnected: boolean;
} {
  return {
    get isConnected(): boolean {
      return owner.isConnected;
    },
  };
}

/**
 * The no-answer mark carried on a thrown error, for the stores that turn a failed result into an
 * exception (the Glass certificate store, say): the page's catch asks `isUnansweredError` and
 * presents nothing, exactly as `isUnanswered` does for a result it holds itself.
 */
export class UnansweredRequestError extends Error {
  /** `message` is the one the store threw before the mark existed, so any reader sees it unchanged. */
  constructor(message: string) {
    super(message);
    this.name = 'UnansweredRequestError';
  }
}

/** Whether a caught error is a request that got no answer. */
export function isUnansweredError(error: unknown): boolean {
  return error instanceof UnansweredRequestError;
}

/**
 * The error a store throws for a failed result: the Error it always threw, with the server's
 * message or `fallback` when it sent none, and for a result that got no answer the same message on
 * an `UnansweredRequestError`, which is still an Error, so an unguarded catch reads it as before.
 */
export function requestFailure(
  result: { success: boolean; unanswered?: boolean; error?: string },
  fallback: string
): Error {
  const message = result.error || fallback;
  if (isUnanswered(result)) return new UnansweredRequestError(message);
  return new Error(message);
}

/**
 * Whether a failed result got no answer from the server. Pages ask this before presenting a
 * failure: a marked one is the connection monitor's to present, so the page shows nothing of its
 * own and keeps its state as it was before the action (D-144). Answered failures, successes and a
 * missing result are all false, so a page falls through to its usual handling.
 */
export function isUnanswered(
  result: { success: boolean; unanswered?: boolean } | null | undefined
): boolean {
  return !!result && !result.success && result.unanswered === true;
}
